import { getLocale } from '../lib/i18n';
import { PaidPurchaseManager } from '../components/PaidPurchaseManager';
import { PaidOfferEditor } from '../components/PaidOfferEditor';
import {tx, ptx, usePageText} from '../lib/page-text';
import { StreamDestinations } from '../components/StreamDestinations';
import { PresenterControls as ScreenShareControls } from '../components/PresenterControls';
import { BackstagePanel } from '../components/program/BackstagePanel';
import { ProducerPanel, useProgramPermissions } from '../components/program/ProducerPanel';
import { ProgramWorkspace } from '../components/program/ProgramWorkspace';
import { ReplayLibrary } from '../components/ReplayLibrary';
import { useBroadcastControl } from '../hooks/useBroadcastControl';
import { useBroadcastNavigation } from '../hooks/useBroadcastNavigation';
import { BroadcastNotice, EndBroadcastDialog } from '../components/BroadcastControl';
import { lazy, Suspense, useEffect, useState } from 'react';
import { CatalogPage } from './Catalog';
import { EventArtwork } from '../components/EventArtwork';
import { EventTools } from '../components/EventTools';
import { EventReports } from '../components/EventReports';
import { solutionForStream, type SolutionTool } from '../../../api/src/solutions/solution-catalog';
import { StreamWorkspace } from '../components/StreamWorkspace';
import { PresentationControls, PresenterInset } from '../components/PresentationControls';
import { InteractiveBoard } from '../components/InteractiveBoard';
import { isAxiosError } from 'axios';
import { Link, useNavigate, useParams, useSearchParams } from '../lib/localized-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { Alert, Code, CodeBlock, Modal, StatusBadge, StepHeading } from '../components/ui';
import { ExternalIcon } from '../components/icons';
import { WhepPlayer } from '../components/WhepPlayer';
import { ChatPanel } from '../components/ChatPanel';
import { useWhipPublisher, type WhipPublisher } from '../components/WhipPublisher';

const PaidLiveStudio = import.meta.env.VITE_PAID_LIVE_UI_ENABLED === 'true' ? lazy(() => import('../components/PaidLiveStudio').then(m => ({ default: m.PaidLiveStudio }))) : () => null;

const RTMPS_INGEST = 'rtmps://ingest.kardocloud.com:443/live';  // TLS-terminating NLB → SRS
const RTMP_INGEST  = 'rtmp://ingest.kardocloud.com:1935/live';  // plain TCP for clients without RTMPS support

/**
 * One stream, laid out around what a person does rather than how the system
 * works: get ready → go live → run the show → share the replay.
 *
 * It used to be nine tabs (Go live, Setup & broadcast, Share, Live chat,
 * Interactive, Restream, Sessions, Recordings, Settings) where chat, polls,
 * links and the preview each appeared twice and nothing said where to start.
 * Now there are four, the header owns the single Go live / End stream button,
 * and the Broadcast tab changes shape with the stream's state.
 */
const PaidHostReplays=import.meta.env.VITE_PAID_HOST_REPLAY_ENABLED==='true'?lazy(()=>import('../components/PaidHostReplays').then(m=>({default:m.PaidHostReplays}))):()=>null;
type Tab = 'destinations' | 'prepare' | 'broadcast' | 'board' | 'share' | 'replays' | 'settings' | 'tools' | 'reports' | 'products';
type Method = 'browser' | 'studio' | 'encoder';

const LATENCY_LABEL: Record<string, string> = {
  REALTIME: 'Real-time · under 0.3 s delay',
  LOW_LATENCY: 'Low latency · 1–3 s delay',
  BROADCAST: 'Broadcast · 5–15 s delay',
};

function fmtDuration(ms: number) {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
}

function publicViewerOrigin() {
  // The console host rewrites watch/embed URLs to its SPA; public viewers live on the apex.
  return typeof window === 'undefined' || window.location.hostname === 'live.kardocloud.com'
    ? 'https://kardocloud.com' : window.location.origin;
}

function viewerUrlFor(streamKey: string, name: string) {
  const origin = publicViewerOrigin();
  return `${origin}/watch/?key=${encodeURIComponent(streamKey)}&title=${encodeURIComponent(name)}`;
}

export function StreamDetailPage() {
  usePageText();
  const { id } = useParams();
  // All hooks declared unconditionally, before the loading/not-found returns
  // (an early return above a hook once blanked this page on every load).
  // ?tab=replays etc. — the Overview checklist links straight to a tab.
  const [params, setParams] = useSearchParams();
  const candidate = params.get('tab');
  const tab: Tab = ['destinations', 'prepare', 'broadcast', 'board', 'share', 'replays', 'settings', 'tools', 'reports', 'products'].includes(candidate ?? '') ? candidate as Tab : 'broadcast';
  const setTab = (next: Tab) => setParams(previous => { const p = new URLSearchParams(previous); p.set('tab', next); return p; });
  const [toolRequest, setToolRequest] = useState<{ tool: SolutionTool; sequence: number } | null>(null);
  const [commerceSection, setCommerceSection] = useState<'products' | 'orders'>('products');
  const openTool = (tool: SolutionTool) => {
    if (tool === 'catalog' || tool === 'orders') { setCommerceSection(tool === 'orders' ? 'orders' : 'products'); setTab('products'); return; }
    if (tool === 'board' || tool === 'replays') { setTab(tool); return; }
    setTab('broadcast'); setToolRequest({ tool, sequence: Date.now() });
  };
  useEffect(() => {
    if (!toolRequest || tab !== 'broadcast') return;
    const target = toolRequest.tool === 'products' ? 'event-products' : toolRequest.tool === 'guests' ? 'event-guests' : 'event-audience';
    document.getElementById(target)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [toolRequest, tab]);
  const [method, setMethodState] = useState<Method | null>(null);
  const [boardVisited, setBoardVisited] = useState(tab === 'board');
  useEffect(() => { if (tab === 'board') setBoardVisited(true); }, [tab]);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [paidPublishing, setPaidPublishing] = useState(false);
  const [now, setNow] = useState(Date.now());
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const archive = useMutation({
    mutationFn: async (action: 'archive' | 'restore') => (await api.post(`/me/streams/${id}/${action}`)).data,
    onSuccess: (_d, action) => {
      qc.invalidateQueries({ queryKey: ['my-streams'] });
      qc.invalidateQueries({ queryKey: ['stream', id] });
      if (action === 'archive') navigate('/streams');
    },
  });

  const detail = useQuery({
    queryKey: ['stream', id],
    queryFn: async () => (await api.get(`/me/streams/${id}`)).data,
    enabled: !!id,
    refetchInterval: 5_000,
  });
  const s = detail.data;
  const key = (s?.keys?.[0]?.key as string | undefined) ?? '';
  const control = useBroadcastControl(id);
  const pub = useWhipPublisher(key);
  useBroadcastNavigation(paidPublishing || pub.state === 'live' || pub.state === 'connecting');
  useEffect(() => { if (control.query.data?.blocked) pub.stop(); }, [control.query.data?.blocked, pub.stop]);

  // A stream can be archived in another tab while this page has a preview.
  // Release local capture when that status arrives from the server.
  useEffect(() => {
    if (s?.status === 'ARCHIVED') pub.stop();
  }, [s?.status, pub.stop]);

  // How this person broadcasts is remembered per stream; the first time, a
  // stream that was last fed by RTMP/SRT is assumed to use an encoder.
  useEffect(() => {
    if (!s || method) return;
    let saved: string | null = null;
    try { saved = localStorage.getItem(`kl-method-${s.id}`); } catch { /* private mode */ }
    if (saved === 'browser' || saved === 'studio' || saved === 'encoder') { setMethodState(saved); return; }
    const lastProto = String(s.sessions?.[0]?.protocol ?? '').toUpperCase();
    setMethodState(lastProto === 'RTMP' || lastProto === 'SRT' ? 'encoder' : 'browser');
  }, [s, method]);
  const setMethod = (m: Method) => {
    setMethodState(m);
    try { if (s) localStorage.setItem(`kl-method-${s.id}`, m); } catch { /* ignore */ }
  };

  const isLive = !(control.query.data?.blocked && !control.query.data?.stopping) && s?.status !== 'ARCHIVED' && (s?.status === 'LIVE' || pub.state === 'live');
  useEffect(() => {
    if (!isLive) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isLive]);

  if (detail.isLoading) return <div className="card muted" role="status">{tx("Loading your stream…")}</div>;
  if (!s) {
    const notFound = isAxiosError(detail.error) && detail.error.response?.status === 404;
    return (
      <div>
        <Link to="/streams" className="btn ghost sm">{tx("← All streams")}</Link>
        <Alert kind={notFound ? 'info' : 'error'} title={notFound ? tx("Stream not found") : tx("Could not load this stream")}>
          <p>{notFound ? tx("This stream is unavailable in your workspace. Return to your stream list to choose another.") : tx("Check your connection and try again. A connection problem does not mean your stream was deleted.")}</p>
          {!notFound && <button className="btn outline sm" disabled={detail.isFetching} onClick={() => void detail.refetch()}>
            {detail.isFetching ? tx("Retrying…") : tx("Try again")}
          </button>}
        </Alert>
      </div>
    );
  }

  const activeSession = (s.sessions ?? []).find((x: any) => !x.endedAt);
  const lastEnded = (s.sessions ?? []).find((x: any) => x.endedAt);
  const liveSince = activeSession ? new Date(activeSession.startedAt).getTime() : null;
  const paidUI = import.meta.env.VITE_PAID_LIVE_UI_ENABLED === 'true' && s.blueprintConfig?.access?.mode === 'ticketed';
  const publishingHere = pub.state === 'live';
  const m: Method = method ?? 'browser';

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Link to="/streams" className="btn ghost sm">{tx("← All streams")}</Link>
      </div>

      {/* ── Header: what this is, whether it's on, and the one action that matters ── */}
      <div className="sd-head">
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}><EventArtwork stream={s} compact /><h1 className="h1" style={{ marginBottom: 6 }}>{s.name}</h1></div>
          <div className="flex gap" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {s.status === 'ARCHIVED' ? <StatusBadge status="ARCHIVED" /> : isLive ? (
              <span className="badge live">{ptx("{v0} Live{v1}", {v0: (<span className="pulse" />), v1: (liveSince ? ` · ${fmtDuration(now - liveSince)}` : '')})}</span>
            ) : (
              <span className="badge idle">{tx("Offline")}</span>
            )}
            <span className="muted" style={{ fontSize: 13 }}>
              {solutionForStream(s)?.title ? `${tx(solutionForStream(s)!.title)} · ` : s.blueprint?.name ? `${s.blueprint.name} · ` : ''}{LATENCY_LABEL[s.mode] ? tx(LATENCY_LABEL[s.mode]) : s.mode}
            </span>
          </div>
        </div>
        <div className="flex gap" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn outline" onClick={() => setTab('share')}>{tx("Share")}</button>
          {paidUI && <button className="btn primary" onClick={() => setTab('broadcast')}>{tx('Open protected studio')}</button>}
          {!paidUI && s.status !== 'ARCHIVED' && control.canEdit && !control.query.data?.blocked && (!s.metadata?.automaticPremiereJob || isLive) && <PrimaryAction
            isLive={isLive} publishingHere={publishingHere} method={m} pub={pub} streamId={s.id}
            hasKey={!!key && !!control.query.data && !control.query.isError}
            onGoToBroadcast={() => setTab('broadcast')}
            onEnd={() => setConfirmEnd(true)}
          />}
          <MoreMenu streamId={s.id} viewerUrl={paidUI ? `${location.origin}${import.meta.env.BASE_URL}${getLocale()}/paid/${encodeURIComponent(key)}` : `${publicViewerOrigin()}/v1/events/${encodeURIComponent(s.id)}/share`} onSettings={() => setTab('settings')}
            onArchive={s.status === 'ARCHIVED' ? undefined : () => setConfirmArchive(true)} canArchive={!isLive} />
        </div>
      </div>

      {!paidUI && (s.metadata?.automaticPremiereJob ? <div className="card" style={{marginBottom:16}}><strong>{tx("Scheduled recording playback")}</strong><p>{tx("Manage the playlist, schedule and cancellation in Premieres. This stream accepts its automatic playback worker; camera and encoder publishing are unavailable here.")}</p><Link className="btn outline" to="/premieres">{tx("Manage premiere")}</Link></div> : <BroadcastNotice control={control} onEnd={() => { control.action.reset(); setConfirmEnd(true); }} />)}

      {detail.isError && (
        <Alert kind="warn" title={tx("Stream status could not refresh")}>
          <p>{tx("Showing the last loaded details. The live status may have changed. Your local broadcast has not been stopped.")}</p>
          <button className="btn outline sm" disabled={detail.isFetching} onClick={() => void detail.refetch()}>
            {detail.isFetching ? tx("Retrying…") : tx("Try again")}
          </button>
        </Alert>
      )}

      {s.status === 'ARCHIVED' && (
        <div className="card row-between" style={{ marginBottom: 16, gap: 12 }}>
          <div>
            <strong>{tx("This stream is archived.")}</strong>
            <div className="muted" style={{ fontSize: 13 }}>{tx("It’s hidden from your list and can’t go live. Its replays and history are kept.")}</div>
          </div>
          <button className="btn" disabled={archive.isPending} onClick={() => archive.mutate('restore')}>
            {archive.isPending ? tx("Restoring…") : tx("Restore stream")}
          </button>
        </div>
      )}

      {s.status === 'ARCHIVED' && archive.isError && (
        <Alert title={tx("Could not restore this stream")}>{tx("Check your connection, then select Restore stream again.")}</Alert>
      )}

      {confirmArchive && (
        <Modal onClose={() => setConfirmArchive(false)}>
          <h2 style={{ marginTop: 0 }}>{ptx("Archive “{v0}”?", {v0: (s.name)})}</h2>
          <p className="muted">{ptx("It leaves your stream list and its key stops working. Nothing is deleted — replays and broadcast history stay, and you can restore it any time from {v0}.", {v0: (<strong>{tx("Live streams → Archived")}</strong>)})}</p>
          {archive.isError && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 10 }}>{(archive.error as any)?.response?.data?.message ?? tx("Could not archive the stream.")}</div>}
          <div className="flex gap" style={{ justifyContent: 'flex-end' }}>
            <button className="btn outline" onClick={() => setConfirmArchive(false)}>{tx("Cancel")}</button>
            <button className="btn danger" disabled={archive.isPending} onClick={() => archive.mutate('archive')}>
              {archive.isPending ? tx("Archiving…") : tx("Archive stream")}
            </button>
          </div>
        </Modal>
      )}

      <nav className="stream-workflow" aria-label={tx("Stream workflow")}>
        {([
          ['prepare', 'Prepare', 'Details & settings', ['prepare', 'settings']],
          ['broadcast', 'Studio', 'Broadcast & board', ['broadcast', 'board', 'destinations']],
          ['share', 'Audience', 'Share & engage', ['share', 'tools', 'products']],
          ['replays', 'Results', 'Replays & reports', ['replays', 'reports']],
        ] as const).map(([target, label, description, members], index) => <button key={target} className={`workflow-step ${(members as readonly string[]).includes(tab) ? 'active' : ''}`} aria-current={(members as readonly string[]).includes(tab) ? 'step' : undefined} onClick={() => setTab(target)}><span className="workflow-number">{index + 1}</span><span><strong>{tx(label)}</strong><small>{tx(description)}</small></span></button>)}
      </nav>
      <div className="tabs" aria-label={tx("Workspace tools")}>
        {(['prepare','settings'].includes(tab) ? [['prepare','Details'],['settings','Settings']] : ['broadcast','board','destinations'].includes(tab) ? [['broadcast','Broadcast'], ['destinations','Destinations'], ...(s.blueprintConfig?.features?.whiteboard ? [['board','Interactive board']] : [])] : ['share','tools','products'].includes(tab) ? [['share','Share'],['tools','Tools'], ...(s.blueprintConfig?.commerce?.catalog ? [['products','Products & orders']] : [])] : [['replays','Replays'],['reports','Reports']]).map(([target,label]) => <button key={target} className={`tab ${tab === target ? 'active' : ''}`} onClick={() => setTab(target as Tab)}>{tx(label)}</button>)}
      </div>

      {tab === 'destinations' && <StreamDestinations streamId={s.id} />}
      {tab === 'products' && s.blueprintConfig?.commerce?.catalog && <div><button className="btn ghost sm" onClick={() => setTab('tools')}>{tx("← Event tools")}</button><CatalogPage key={commerceSection} initialSection={commerceSection} streamId={s.id} archived={s.status === 'ARCHIVED'} /></div>}
      {tab === 'products' && !s.blueprintConfig?.commerce?.catalog && <div className="card">{tx("Product tools are not enabled for this event.")}</div>}
      {tab === 'tools' && <><EventTools stream={s} onOpen={openTool} onSettings={() => setTab('settings')} /><ProgramWorkspace privateRoomBlocked={paidPublishing || pub.hasCamera || pub.state === 'connecting' || pub.state === 'live'} stream={s} mode="control" /></>}
      {tab === 'reports' && <><ProgramWorkspace privateRoomBlocked={paidPublishing || pub.hasCamera || pub.state === 'connecting' || pub.state === 'live'} stream={s} mode="report" /><EventReports stream={s} /></>}
      {tab === 'prepare' && <><StreamWorkspace stream={s} onNavigate={setTab} /><ProgramWorkspace privateRoomBlocked={paidPublishing || pub.hasCamera || pub.state === 'connecting' || pub.state === 'live'} stream={s} /></>}
      {!paidUI && boardVisited && s.blueprintConfig?.features?.whiteboard && <div className="presenter-board" style={{ display: tab === 'board' ? 'block' : 'none' }}>{isLive && <PresentationControls streamId={s.id} onShow={mode => setTab(mode === 'board' ? 'board' : 'broadcast')} />}<InteractiveBoard key={s.id} streamId={s.id} /><PresenterInset streamId={s.id} pub={pub} streamKey={key} live={isLive} /></div>}
      {paidUI && import.meta.env.VITE_PAID_BOARD_UI_ENABLED !== 'true' && tab === 'board' && <div className="card">{tx('Protected interactive board presentation is still being prepared. Use camera or screen sharing in Studio.')}</div>}
      {tab === 'board' && !s.blueprintConfig?.features?.whiteboard && <div className="card">{tx("The interactive board is off. ")}<button className="btn outline" onClick={() => setTab('settings')}>{tx("Open feature settings")}</button></div>}

      {/* The Broadcast tab stays mounted (hidden) so switching to Share while
          live never drops the camera, the chat socket or the monitor. */}
      <div style={{ display: tab === 'broadcast' || paidUI && import.meta.env.VITE_PAID_BOARD_UI_ENABLED === 'true' && tab === 'board' ? 'block' : 'none' }}>
        {paidUI ? <Suspense fallback={<div className="card" role="status">{tx('Please wait…')}</div>}><PaidLiveStudio key={s.id} streamId={s.id} archived={s.status === 'ARCHIVED'} onPublishing={setPaidPublishing} boardEnabled={!!s.blueprintConfig?.features?.whiteboard} boardOpen={tab === 'board'} onOpenBoard={()=>setTab('board')} /></Suspense> : s.status === 'ARCHIVED' ? (
          <Alert kind="info" title={tx("Restore this stream before broadcasting")}>{tx("Your replays and broadcast history are available in the Replays tab.")}</Alert>
        ) : key ? (
          <BroadcastTab
            s={s} streamKey={key} pub={pub} toolRequest={toolRequest} method={m} setMethod={setMethod}
            onPresentation={mode => setTab(mode === 'board' ? 'board' : 'broadcast')} isLive={isLive} lastEnded={lastEnded} onReplays={() => setTab('replays')} onShare={() => setTab('share')}
          />
        ) : (
          <Alert kind="warn" title={tx("This stream has no key yet")}>{tx("Rotate a key under Settings to start broadcasting.")}</Alert>
        )}
      </div>

      {!paidUI && tab === 'broadcast' && !s.metadata?.automaticPremiereJob && <ProducerPanel streamId={s.id} pub={pub} />}
      {tab === 'share' && <div className="card" style={{ marginBottom: 16 }}><h3>{tx("Audience access")}</h3><p>{ptx("Current admission: {v0}. Manage access in Settings and use the sharing controls below.", {v0: (<strong>{s.blueprintConfig?.access?.mode ?? tx("public")}</strong>)})}</p><button className="btn outline" onClick={() => setTab('settings')}>{tx("Access settings")}</button><p className="muted">{tx("Configure registration in Prepare → Event tools. Review attendees in Results → Reports.")}</p></div>}
      {paidUI && tab === 'share' && key && <div className="card"><h3>{tx('Protected event link')}</h3><p>{tx('Viewers sign in and their current paid access is checked before viewing.')}</p><a style={{overflowWrap:'anywhere'}} href={`${import.meta.env.BASE_URL}${getLocale()}/paid/${encodeURIComponent(key)}`} target="_blank" rel="noopener">{new URL(`${import.meta.env.BASE_URL}${getLocale()}/paid/${encodeURIComponent(key)}`,location.origin).href}</a></div>}
      {!paidUI && tab === 'share' && key && (
        <ShareTab stream={s} streamName={s.name} streamKey={key} isLive={isLive} streamId={s.id} access={s.blueprintConfig?.access?.mode ?? 'public'} />
      )}

      {tab === 'replays' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {import.meta.env.VITE_PAID_HOST_REPLAY_ENABLED==='true'&&s.blueprintConfig?.access?.mode==='ticketed'?<Suspense fallback={null}><PaidHostReplays streamId={s.id}/></Suspense>:<RecordingsTab streamId={s.id} recordings={s.recordings ?? []} recordingEnabled={s.recordingEnabled} />}
          <BroadcastHistory sessions={s.sessions ?? []} />
        </div>
      )}

      {tab === 'settings' && <SettingsTab s={s} streamKey={key} onRotated={() => qc.invalidateQueries({ queryKey: ['stream', id] })} />}

      {confirmEnd && (
        <EndBroadcastDialog control={control} onClose={() => setConfirmEnd(false)} onStopped={pub.stop} />
      )}
    </div>
  );
}

// ─── Header pieces ────────────────────────────────────────────────

function PrimaryAction({ isLive, publishingHere, method, pub, streamId, hasKey, onGoToBroadcast, onEnd }: {
  isLive: boolean; publishingHere: boolean; method: Method; pub: WhipPublisher; streamId: string; hasKey: boolean;
  onGoToBroadcast: () => void; onEnd: () => void;
}) {
  usePageText();
  if (isLive) {
    return <button className={`btn ${publishingHere ? 'danger' : 'outline'}`} onClick={onEnd}>{tx("End stream")}</button>;
  }
  if (!hasKey) return <button className="btn primary" disabled title={tx("Create a stream key in Settings first.")}>{tx("Stream key needed")}</button>;
  if (method === 'studio') {
    return <Link className="btn primary" to={`/studio?stream=${streamId}`}>{tx("Open Studio")}</Link>;
  }
  if (method === 'encoder') {
    return <span className="badge" title={tx("Start streaming in OBS or your encoder — this page goes live by itself.")}>{tx("Waiting for your encoder…")}</span>;
  }
  if (pub.state === 'connecting') return <button className="btn primary" disabled>{tx("Going live…")}</button>;
  if (pub.hasCamera) return <button disabled={pub.sharePending || pub.devicePending} className="btn primary" onClick={() => { onGoToBroadcast(); pub.goLive(); }}>{tx("● Go live")}</button>;
  return <button className="btn primary" onClick={() => { onGoToBroadcast(); pub.startPreview(); }}>{tx("Turn on camera")}</button>;
}

function MoreMenu({ streamId, viewerUrl, onSettings, onArchive, canArchive }: {
  streamId: string; viewerUrl: string; onSettings: () => void; onArchive?: () => void; canArchive: boolean;
}) {
  usePageText();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  return (
    <div className="sd-menu">
      <button className="btn ghost" aria-label={tx("More actions")} aria-expanded={open} onClick={() => setOpen((v) => !v)}>⋯</button>
      {open && (
        <>
          <button className="sd-menu-catch" aria-hidden tabIndex={-1} onClick={() => setOpen(false)} />
          <div className="sd-menu-panel" role="menu">
            <a href={viewerUrl} target="_blank" rel="noopener" role="menuitem" onClick={() => setOpen(false)}>{ptx("Open viewer page {v0}", {v0: (<ExternalIcon />)})}</a>
            <button role="menuitem" onClick={() => { navigator.clipboard?.writeText(streamId); setCopied(true); setTimeout(() => { setCopied(false); setOpen(false); }, 900); }}>
              {copied ? tx("Copied ✓") : tx("Copy stream ID")}
            </button>
            <button role="menuitem" onClick={() => { setOpen(false); onSettings(); }}>{tx("Stream settings & key")}</button>
            {onArchive && (
              <button role="menuitem" disabled={!canArchive} title={canArchive ? undefined : tx("End the broadcast first")}
                onClick={() => { setOpen(false); onArchive(); }}>{tx("Archive stream…")}</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Broadcast tab ────────────────────────────────────────────────

function BroadcastTab({ s, streamKey, pub, toolRequest, method, setMethod, isLive, lastEnded, onReplays, onShare, onPresentation }: {
  toolRequest: { tool: SolutionTool; sequence: number } | null; s: any; streamKey: string; pub: WhipPublisher; method: Method; setMethod: (m: Method) => void;
  onPresentation: (mode: 'video' | 'board') => void; isLive: boolean; lastEnded?: any; onReplays: () => void; onShare: () => void;
}) {
  usePageText();
  const blueprint = s.blueprintConfig ? { ...s.blueprintConfig, name: s.blueprint?.name, archetype: s.blueprint?.archetype } : undefined;

  return (
    <div className="sd-grid">
      <div className="sd-main">
        {isLive ? (
          <LiveNow s={s} pub={pub} onPresentation={onPresentation} />
        ) : (
          <>
            {lastEnded && <LastBroadcast session={lastEnded} recordings={s.recordings?.length ?? 0} onReplays={onReplays} />}
            {s.metadata?.automaticPremiereJob ? <div className="card"><h3>{tx("Automatic playback")}</h3><p>{tx("The saved recordings start at the scheduled time when the worker is ready. Check the premiere for current progress or errors.")}</p><Link className="btn" to="/premieres">{tx("Open Premieres")}</Link></div> : <GetReady s={s} streamKey={streamKey} pub={pub} method={method} setMethod={setMethod} onShare={onShare} />}
          </>
        )}

        {blueprint?.commerce?.catalog && (blueprint?.viewer?.overlays ?? []).includes('product-card') && (
          <div id="event-products"><FeaturedProduct streamId={s.id} /></div>
        )}
        {(blueprint?.capture?.publishers ?? 1) > 1 && (
          <div id="event-guests"><BackstagePanel streamId={s.id} legacy={<RoomPanel streamId={s.id} seats={blueprint.capture.publishers} />} /></div>
        )}

      </div>

      <AudienceRail streamId={s.id} streamKey={streamKey} blueprint={blueprint} toolRequest={toolRequest} />
    </div>
  );
}

/** Offline: three steps, top to bottom. */
function GetReady({ s, streamKey, pub, method, setMethod, onShare }: {
  s: any; streamKey: string; pub: WhipPublisher; method: Method; setMethod: (m: Method) => void; onShare: () => void;
}) {
  usePageText();
  const viewerUrl = `${publicViewerOrigin()}/v1/events/${encodeURIComponent(s.id)}/share`;
  const [copied, setCopied] = useState(false);
  return (
    <div className="card sd-steps">
      <div>
        <StepHeading step={1} title={tx("How will you broadcast?")} done={!!method} />
        <div className="sd-methods">
          <MethodCard active={method === 'browser'} onClick={() => setMethod('browser')} title={tx("This browser")} tag="Easiest"
            desc={tx("Your camera and microphone, straight from this tab. Nothing to install.")} />
          <MethodCard active={method === 'studio'} onClick={() => setMethod('studio')} title={tx("Studio")}
            desc={tx("Share your screen or slides with your camera in the corner.")} />
          <MethodCard active={method === 'encoder'} onClick={() => setMethod('encoder')} title={tx("OBS or an encoder")}
            desc={tx("For produced shows: OBS, vMix, a hardware encoder, a phone app.")} />
        </div>
      </div>

      <div>
        {method === 'browser' && (
          <>
            <StepHeading step={2} title={tx("Check your camera and microphone")} done={pub.hasCamera} />
            <div className="sd-cam">
              <video ref={pub.videoRef} autoPlay playsInline muted style={{ transform: pub.sharing ? undefined : 'scaleX(-1)' }} />
              {!pub.hasCamera && (
                <div className="sd-cam-empty">
                  <button className="btn primary" onClick={pub.startPreview}>{tx("Turn on camera")}</button>
                  <span className="muted" style={{ fontSize: 12 }}>{tx("Your browser will ask for permission. Nobody sees you until you go live.")}</span>
                </div>
              )}
            </div>
            {pub.hasCamera && <ScreenShareControls pub={pub} />}
            {pub.error && <div style={{ marginTop: 10 }}><Alert kind="error" title={tx("Something went wrong")}>{pub.error}</Alert></div>}
            {pub.hasCamera && (
              <div className="row-between" style={{ marginTop: 10, gap: 12, flexWrap: 'wrap' }}>
                <span className="muted" style={{ fontSize: 13 }}>{ptx("Camera {v0} · Microphone {v1}{v2}. Looking good?", {v0: (pub.cameraEnabled ? tx("on") : tx("off")), v1: (pub.microphoneEnabled ? tx("on") : tx("muted")), v2: (pub.stats.resolution !== '—' ? ` · ${pub.stats.resolution}` : '')})}</span>
                <div className="flex gap">
                  <button className="btn ghost" onClick={pub.stop}>{tx("Turn off")}</button>
                  <button className="btn primary lg" disabled={pub.state === 'connecting' || pub.sharePending || pub.devicePending} onClick={pub.goLive}>
                    {pub.state === 'connecting' ? tx("Going live…") : tx("● Go live")}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {method === 'studio' && (
          <>
            <StepHeading step={2} title={tx("Open Studio")} />
            <p className="muted" style={{ fontSize: 13.5, margin: '0 0 12px' }}>{tx("Studio mixes your camera, screen and slides into one picture and broadcasts it to this stream.")}</p>
            <Link className="btn primary" to={`/studio?stream=${s.id}`}>{tx("Open Studio for this stream")}</Link>
          </>
        )}

        {method === 'encoder' && (
          <>
            <StepHeading step={2} title={tx("Connect your encoder")} />
            <p className="muted" style={{ fontSize: 13.5, margin: '0 0 12px' }}>{ptx("In OBS: {v0}, paste these two values, then {v1}. This page goes live by itself when the video arrives.", {v0: (<strong>{tx("Settings → Stream → Service: Custom")}</strong>), v1: (<strong>{tx("Start Streaming")}</strong>)})}</p>
            <label className="label">{tx("Server")}</label>
            <Code value={RTMPS_INGEST} />
            <label className="label" style={{ marginTop: 10 }}>{ptx("Stream key {v0}", {v0: (<span className="muted">{tx("— keep this private")}</span>)})}</label>
            <Code value={streamKey} />
            <details style={{ marginTop: 10 }}>
              <summary className="muted" style={{ fontSize: 12.5, cursor: 'pointer' }}>{tx("Encoder can’t do TLS, or want a quick test?")}</summary>
              <div style={{ marginTop: 8 }}>
                <label className="label">{tx("Plain RTMP server")}</label>
                <Code value={RTMP_INGEST} />
                <label className="label" style={{ marginTop: 10 }}>{tx("Test pattern from a terminal (ffmpeg)")}</label>
                <CodeBlock value={`ffmpeg -re -f lavfi -i "testsrc2=size=1280x720:rate=30" \\
       -f lavfi -i "sine=frequency=440" \\
       -c:v libx264 -preset veryfast -tune zerolatency -g 60 \\
       -c:a aac -b:a 128k -f flv \\
       ${RTMPS_INGEST}/${streamKey}`} />
              </div>
            </details>
          </>
        )}
      </div>

      <div>
        <StepHeading step={3} title={tx("Share the link")} />
        <p className="muted" style={{ fontSize: 13.5, margin: '0 0 10px' }}>{tx("Send it now — people who open it early see a waiting screen and join automatically when you go live.")}</p>
        <div className="flex gap" style={{ flexWrap: 'wrap' }}>
          <button className="btn outline" onClick={() => { navigator.clipboard?.writeText(viewerUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
            {copied ? tx("Copied ✓") : tx("Copy viewer link")}
          </button>
          <a className="btn ghost" href={viewerUrl} target="_blank" rel="noopener">{ptx("Open viewer page {v0}", {v0: (<ExternalIcon />)})}</a>
          <button className="btn ghost" onClick={onShare}>{tx("More ways to share →")}</button>
        </div>
      </div>
    </div>
  );
}

function MethodCard({ active, onClick, title, desc, tag }: { active: boolean; onClick: () => void; title: string; desc: string; tag?: string }) {
  usePageText();
  return (
    <button type="button" className={`sd-method ${active ? 'active' : ''}`} onClick={onClick} aria-pressed={active}>
      <span className="flex gap" style={{ alignItems: 'center' }}>
        <strong>{title}</strong>
        {tag && <span className="badge info" style={{ fontSize: 10.5, padding: '1px 7px' }}>{tag}</span>}
      </span>
      <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.45 }}>{desc}</span>
    </button>
  );
}

function LiveNow({ s, pub, onPresentation }: { s: any; pub: WhipPublisher; onPresentation: (mode: 'video' | 'board') => void }) {
  usePageText();
  const kbps = pub.stats.kbps;
  const health = pub.state !== 'live' ? null
    : kbps >= 800 ? { kind: 'success', text: 'Good connection' }
    : kbps >= 250 ? { kind: 'warn', text: 'Weak upload — viewers may see lower quality' }
    : { kind: 'warn', text: 'Upload is struggling — check your network' };
  return (
    <>
      <div className="card">
        <div className="row-between" style={{ marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>{tx("What viewers see")}</h3>
          {health && <span className={`badge ${health.kind}`}>{health.text}</span>}
        </div>
        <WhepPlayer streamId={s.id} streamKey={s.keys?.[0]?.key} autoRetry streamLive />
        {s.blueprintConfig?.features?.whiteboard && <PresentationControls streamId={s.id} onShow={onPresentation} />}
      </div>
      {pub.state === 'live' && (
        <div className="card sd-selfview" style={{ flexWrap: 'wrap' }}>
          <video ref={pub.videoRef} autoPlay playsInline muted style={{ transform: pub.sharing ? undefined : 'scaleX(-1)' }} />
          <div>
            <strong style={{ fontSize: 14 }}>{pub.sharing ? tx("Your screen & camera") : tx("Your camera")}</strong>
            <ScreenShareControls pub={pub} />
            <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>{tx("Broadcasting from this browser")}{pub.stats.resolution !== '—' ? ` · ${pub.stats.resolution}` : ''}{kbps ? ptx(" · {v0} Mbps", {v0: ((kbps / 1000).toFixed(1))}) : ''}
            </div>
            {pub.error && <div style={{ fontSize: 12.5, color: 'var(--warning)', marginTop: 6 }}>{pub.error}</div>}
          </div>
        </div>
      )}
    </>
  );
}

/** After a broadcast: how it went, and where the replay is. */
function LastBroadcast({ session, recordings, onReplays }: { session: any; recordings: number; onReplays: () => void }) {
  usePageText();
  const dur = new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime();
  return (
    <div className="card sd-last">
      <div>
        <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em' }}>{tx("Last broadcast")}</div>
        <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>
          {new Date(session.startedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
        </div>
      </div>
      <div className="sd-last-stats">
        <span><strong>{fmtDuration(dur)}</strong><br /><span className="muted">{tx("duration")}</span></span>
        <span><strong>{session.peakViewers ?? 0}</strong><br /><span className="muted">{tx("peak viewers")}</span></span>
        {session.protocol && <span><strong>{session.protocol}</strong><br /><span className="muted">{tx("source")}</span></span>}
      </div>
      <button className="btn outline sm" onClick={onReplays}>{recordings ? tx("Watch the replay") : tx("Broadcast history")} →</button>
    </div>
  );
}

/** Chat and Polls & Q&A, side by side with the show — the only place they live now. */
function AudienceRail({ streamId, streamKey, blueprint, toolRequest }: { streamId: string; streamKey: string; blueprint?: any; toolRequest: { tool: SolutionTool; sequence: number } | null }) {
  usePageText();
  const f = blueprint?.features ?? {};
  const showChat = f.chat !== false;
  const showInteractive = f.polls === true || f.qa === true || f.handRaise === true || f.reactions === true;
  const [rail, setRail] = useState<'chat' | 'interactive'>(showChat ? 'chat' : 'interactive');
  useEffect(() => { if (toolRequest?.tool === 'chat') setRail('chat'); else if (toolRequest?.tool === 'polls' || toolRequest?.tool === 'qa') setRail('interactive'); }, [toolRequest]);
  useEffect(() => { if (rail === 'chat' && !showChat && showInteractive) setRail('interactive'); if (rail === 'interactive' && !showInteractive && showChat) setRail('chat'); }, [rail, showChat, showInteractive]);
  return (
    <div className="card sd-rail" id="event-audience">
      <div className="flex gap" style={{ marginBottom: 10 }}>
        {showChat && <button className={`btn sm ${rail === 'chat' ? 'primary' : 'ghost'}`} onClick={() => setRail('chat')}>{tx("Chat")}</button>}
        {showInteractive && <button className={`btn sm ${rail === 'interactive' ? 'primary' : 'ghost'}`} onClick={() => setRail('interactive')}>{tx("Polls & Q&A")}</button>}
        {!showChat && !showInteractive && <span className="muted" style={{ fontSize: 12 }}>{tx("This kind of stream runs without an audience panel.")}</span>}
      </div>
      {/* Both stay mounted: switching to polls must not drop the chat socket. */}
      {showChat && (
        <div style={{ display: rail === 'chat' ? 'block' : 'none', flex: 1, minHeight: 0 }}>
          <ChatPanel streamId={streamId} streamKey={streamKey} />
        </div>
      )}
      {showInteractive && (
        <div style={{ display: rail === 'interactive' ? 'block' : 'none', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <InteractiveTab streamId={streamId} features={f} />
        </div>
      )}
    </div>
  );
}

// ─── Share tab ────────────────────────────────────────────────────

function ShareTab({ streamName, streamKey, isLive, streamId, access, stream }: { streamName: string; streamKey: string; isLive: boolean; streamId: string; access: string; stream: any }) {
  usePageText();
  const origin = publicViewerOrigin();
  const viewerUrl = `${origin}/v1/events/${encodeURIComponent(streamId)}/share`;
  const hlsUrl    = `${origin}/live/${streamKey}.m3u8`;
  const embedTag  = `<iframe src="${origin}/embed/player.html?src=${encodeURIComponent(hlsUrl)}" width="640" height="360" allowfullscreen frameborder="0"></iframe>`;
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {access !== 'public' && <InviteLink streamId={streamId} access={access} />}

      <div className="card">
        <div className="row-between" style={{ marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>{tx("Viewer page")}</h3>
          <span className={`badge ${isLive ? 'live' : 'idle'}`}>{isLive ? tx("Live now") : tx("Offline")}</span>
        </div>
        <p className="muted" style={{ fontSize: 13.5, marginBottom: 12 }}>{ptx("{v0} The link shows your event cover and description. Visitors select Watch live event to open the player with this event’s product cards, polls and audience tools. The player waits for your broadcast when you are offline.", {v0: (access === 'public' ? tx("A public viewer page — no account needed.") : tx("A viewer page protected by this event’s admission settings. Use the invitation controls above for invite-only access."))})}</p>
        {stream.metadata?.eventWorkspace?.coverImageUrl && <div style={{ maxWidth: 440, marginBottom: 14 }}><EventArtwork stream={stream} /><strong>{streamName}</strong><p className="muted" style={{ fontSize: 12 }}>{tx("Link preview. Messaging apps may cache an earlier image. Private events require a valid invite.")}</p></div>}
        <Code value={viewerUrl} large />
        <div className="flex gap" style={{ marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn primary" onClick={() => { navigator.clipboard?.writeText(viewerUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
            {copied ? tx("Copied ✓") : tx("Copy link")}
          </button>
          <a className="btn outline" href={viewerUrl} target="_blank" rel="noopener">{ptx("Open viewer page {v0}", {v0: (<ExternalIcon />)})}</a>
          <button className="btn outline" onClick={() => {
            if (navigator.share) navigator.share({ title: streamName, url: viewerUrl }).catch(() => {});
            else navigator.clipboard?.writeText(viewerUrl);
          }}>{tx("Share…")}</button>
        </div>
      </div>

      <details className="card sd-details">
        <summary><strong>{tx("Put it on your own website")}</strong><span className="muted">{tx(" — embed code and direct links")}</span></summary>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 14 }}>
          <div>
            <label className="label">{tx("Embed the player (auto-plays muted, fullscreen allowed)")}</label>
            <CodeBlock value={embedTag} />
          </div>
          <div>
            <label className="label">{tx("Direct HLS link (VLC, Safari, any HLS player — about 12 s behind)")}</label>
            <CodeBlock value={hlsUrl} />
          </div>
        </div>
      </details>
    </div>
  );
}

// ─── Replays tab: broadcast history ───────────────────────────────

function BroadcastHistory({ sessions }: { sessions: any[] }) {
  usePageText();
  return (
    <div className="card dashboard-table" style={{ padding: 0 }}>
      <div style={{ padding: '14px 18px 4px' }}><h3 style={{ margin: 0 }}>{tx("Broadcast history")}</h3></div>
      {sessions.length > 0 ? (
        <table>
          <thead><tr><th>{tx("Started")}</th><th>{tx("Length")}</th><th>{tx("Peak viewers")}</th><th>{tx("Source")}</th><th>{tx("Ended")}</th></tr></thead>
          <tbody>
            {sessions.map((sess: any) => {
              const dur = sess.endedAt ? new Date(sess.endedAt).getTime() - new Date(sess.startedAt).getTime() : null;
              return (
                <tr key={sess.id}>
                  <td className="muted">{new Date(sess.startedAt).toLocaleString()}</td>
                  <td>{dur != null ? fmtDuration(dur) : <span style={{ color: 'var(--danger)' }}>{tx("live now")}</span>}</td>
                  <td>{sess.peakViewers ?? 0}</td>
                  <td><span className="badge">{sess.protocol ?? '—'}</span></td>
                  <td className="muted">{sess.forceStopped ? <span className="badge warn">{tx("stopped by an admin")}</span> : sess.endedAt ? tx("normally") : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="muted" style={{ textAlign: 'center', padding: 28 }}>{tx("No broadcasts yet — go live and they’ll be listed here.")}</div>
      )}
    </div>
  );
}

// ─── Settings tab ─────────────────────────────────────────────────

/** One on/off setting. `locked` explains why a switch can't be flipped —
 *  shown instead of hiding the setting, so its current state stays visible. */
function SettingSwitch({ label, desc, on, onChange, pending, locked }: {
  label: string; desc: string; on: boolean; onChange?: (v: boolean) => void; pending?: boolean; locked?: string;
}) {
  usePageText();
  const disabled = !!locked || pending || !onChange;
  return (
    <div className={`sd-switch-row ${locked ? 'locked' : ''}`}>
      <div className="sd-switch-text">
        <span className="name">{label}</span>
        <span className="desc">{desc}</span>
        {locked && <span className="lock">{locked}</span>}
      </div>
      <button
        type="button" role="switch" aria-checked={on} aria-label={label}
        className={`sd-switch ${on ? 'on' : ''} ${pending ? 'pending' : ''}`}
        disabled={disabled}
        onClick={() => onChange?.(!on)}
      />
    </div>
  );
}

function SettingsTab({ s, streamKey, onRotated }: { s: any; streamKey: string; onRotated: () => void }) {
  usePageText();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['stream', s.id] });
  const rotate = useMutation({
    mutationFn: async () => (await api.post(`/me/streams/${s.id}/rotate-key`)).data,
    onSuccess: onRotated,
  });
  const update = useMutation({
    mutationFn: async (patch: { name?: string; chatEnabled?: boolean }) => (await api.patch(`/me/streams/${s.id}`, patch)).data,
    onSuccess: refresh,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not save that change'),
  });
  const captionReadiness = useQuery<{available:boolean;reason:string|null}>({queryKey:['caption-readiness'],queryFn:async()=>(await api.get('/me/streams/transcription/readiness')).data,refetchInterval:30000});
  const transcription = useMutation({
    mutationFn: async (enable: boolean) => (await api.post(`/me/streams/${s.id}/transcription/${enable ? 'start' : 'stop'}`)).data,
    onSuccess: refresh,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not change captions'),
  });
  const [name, setName] = useState(s.name);
  useEffect(() => setName(s.name), [s.name]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <StreamWorkspace stream={s} featuresOnly />
      {import.meta.env.VITE_PAID_ORGANIZER_ENABLED === 'true' && <PaidPurchaseManager key={s.id} streamId={s.id} />}
      {import.meta.env.VITE_PAID_OFFERS_ENABLED === 'true' && s.blueprintConfig?.access?.mode === 'ticketed' && <PaidOfferEditor key={s.id} streamId={s.id} />}
      <div className="card">
        <h3>{tx("Stream name")}</h3>
        <form className="flex gap" style={{ flexWrap: 'wrap' }} onSubmit={(e) => { e.preventDefault(); if (name.trim().length >= 2 && name.trim() !== s.name) update.mutate({ name: name.trim() }); }}>
          <input className="input" style={{ flex: 1, minWidth: 220 }} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} aria-label={tx("Stream name")} />
          <button className="btn primary" disabled={name.trim().length < 2 || name.trim() === s.name || update.isPending}>
            {update.isPending && update.variables?.name ? tx("Saving…") : tx("Save")}
          </button>
        </form>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 4 }}>{tx("Features")}</h3>
        <SettingSwitch
          label={tx("Live captions")} desc={tx("Speech appears as caption messages in live chat. Review automatic text for accuracy.")}
          locked={!s.transcriptionEnabled || !s.metadata?.transcriptionControl?.generation ? captionReadiness.isPending ? 'Checking availability…' : !captionReadiness.data?.available ? captionReadiness.data?.reason ?? 'Caption availability could not be checked.' : s.status!=='LIVE' ? 'Start the broadcast before enabling captions.' : undefined : undefined}
          on={!!s.transcriptionEnabled && s.metadata?.transcriptionControl?.state==='running'} pending={transcription.isPending}
          onChange={(v) => transcription.mutate(v)}
        />
        <SettingSwitch label={tx("Recording")} desc={tx("Keep a replay of each broadcast.")} on={!!s.recordingEnabled} locked="Set when the stream is created — can’t be changed here yet." />
        <SettingSwitch label={tx("Moderation")} desc={tx("Filter abusive chat messages automatically.")} on={!!s.moderationEnabled} locked="Can’t be changed here yet." />
        <SettingSwitch label={tx("Content protection (DRM)")} desc={tx("License-based playback protection.")} on={!!s.drmEnabled} locked="Unavailable until the playback protection service is connected." />
      </div>

      <div className="card">
        <h3>{tx("About this stream")}</h3>
        <div className="sd-facts">
          <div><span className="muted">{tx("Kind of stream")}</span><strong>{s.blueprint?.name ?? tx("Custom")}</strong></div>
          <div><span className="muted">{tx("Delay")}</span><strong>{LATENCY_LABEL[s.mode] ? tx(LATENCY_LABEL[s.mode]) : s.mode}</strong></div>
          <div><span className="muted">{tx("Created")}</span><strong>{new Date(s.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</strong></div>
        </div>
      </div>

      {streamKey && (
        <div className="card">
          <h3>{tx("Broadcast software (OBS, encoders)")}</h3>
          <label className="label">{tx("Server")}</label>
          <Code value={RTMPS_INGEST} />
          <label className="label" style={{ marginTop: 10 }}>{tx("Stream key — anyone with it can broadcast to this stream")}</label>
          <Code value={streamKey} />
        </div>
      )}

      <div className="card sd-danger">
        <h3 style={{ color: 'var(--danger)' }}>{tx("Danger zone")}</h3>
        <div className="row-between" style={{ gap: 16, flexWrap: 'wrap' }}>
          <div style={{ maxWidth: 560 }}>
            <strong style={{ fontSize: 14 }}>{tx("Rotate stream key")}</strong>
            <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{tx("Makes a new key and retires the old one. Anything broadcasting with the old key is cut off at once — use it if the key leaked, or to stop a broadcast you can’t reach.")}</div>
          </div>
          <button className="btn outline" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} disabled={rotate.isPending}
                  onClick={() => { if (confirm('Rotate the key? Future connections need the new key. Use End stream to disconnect a current broadcast.')) rotate.mutate(); }}>
            {rotate.isPending ? tx("Rotating…") : tx("Rotate key")}
          </button>
        </div>
        <div className="row-between" style={{ gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
          <div>
            <strong style={{ fontSize: 14 }}>{tx("Stream ID")}</strong>
            <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{tx("For the API and support requests.")}</div>
          </div>
          <Code value={s.id} />
        </div>
      </div>
    </div>
  );
}

/** Pin one product on screen for viewers. The card only appears for blueprints
 *  that declare a product-card overlay, and the API enforces the same rule —
 *  pinning on a stream without that overlay would show nobody anything. */
function FeaturedProduct({ streamId }: { streamId: string }) {
  usePageText();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const products = useQuery({
    queryKey: ['catalog-products'],
    queryFn: async () => (await api.get('/me/catalog/products')).data,
  });
  const detail = useQuery({
    queryKey: ['stream', streamId],
    queryFn: async () => (await api.get(`/me/streams/${streamId}`)).data,
  });

  const feature = useMutation({
    mutationFn: async (productId: string | null) =>
      (await api.post(`/me/streams/${streamId}/feature-product`, { productId })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stream', streamId] }),
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not pin that product'),
  });

  const items: any[] = products.data?.items ?? products.data ?? [];
  const current = detail.data?.featuredProductId ?? '';

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{tx("On screen now")}</h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>{tx("The product card viewers see over the video. Change it as you talk through the catalog.")}</p>
      {items.length === 0 ? (
        <div className="muted" style={{ fontSize: 13 }}>{tx("No products yet — add them under Catalog and they will show up here.")}</div>
      ) : (
        <div className="flex gap" style={{ flexWrap: 'wrap' }}>
          <select className="input" value={current} style={{ maxWidth: 260 }}
                  onChange={(e) => { setErr(null); feature.mutate(e.target.value || null); }}>
            <option value="">{tx("Nothing pinned")}</option>
            {items.map((p: any) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {current && (
            <button className="btn outline sm" disabled={feature.isPending}
                    onClick={() => { setErr(null); feature.mutate(null); }}>{tx("Clear")}</button>
          )}
        </div>
      )}
      {err && <div style={{ color: 'var(--danger, #f31260)', fontSize: 13, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

/** Restricted streams need a link that carries permission, not just the key.
 *  The token expires, so forwarding it lends access for a while rather than
 *  handing it over permanently. */
function InviteLink({ streamId, access }: { streamId: string; access: string }) {
  usePageText();
  const [minutes, setMinutes] = useState(240);
  const [issued, setIssued] = useState<{ url: string; expiresAt: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mint = useMutation({
    mutationFn: async () => (await api.post(`/me/streams/${streamId}/invite`, { minutes })).data,
    onSuccess: (d) => { setIssued(d); setCopied(false); },
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not create a link'),
  });

  const unsupported = access !== 'invite';

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>{tx("Invite link")}</h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>{ptx("This stream's blueprint restricts who can watch ({v0}). The plain share link below will not open it — viewers need a link carrying a token, and playback is refused at the media server without one.", {v0: (access)})}</p>
      {unsupported ? (
        <div style={{ fontSize: 13, color: 'var(--warning, #f5a524)' }}>
          {access}{tx(" access is not built yet, so this stream cannot be opened by anyone. Switch the blueprint to invite or public until it is.")}</div>
      ) : (
        <>
          <div className="flex gap" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <label className="muted" style={{ fontSize: 12 }}>{tx("Valid for")}</label>
            <select className="input" style={{ maxWidth: 150 }} value={minutes}
                    onChange={(e) => setMinutes(Number(e.target.value))}>
              <option value={60}>{tx("1 hour")}</option>
              <option value={240}>{tx("4 hours")}</option>
              <option value={1440}>{tx("1 day")}</option>
              <option value={10080}>{tx("7 days")}</option>
            </select>
            <button className="btn" disabled={mint.isPending} onClick={() => { setErr(null); mint.mutate(); }}>
              {mint.isPending ? tx("Creating…") : tx("Create invite link")}
            </button>
          </div>
          {issued && (
            <div style={{ marginTop: 12 }}>
              <Code value={issued.url} />
              <div className="flex gap" style={{ marginTop: 8, alignItems: 'center' }}>
                <button className="btn outline sm" onClick={() => {
                  navigator.clipboard?.writeText(issued.url).then(() => setCopied(true)).catch(() => setCopied(false));
                }}>{copied ? tx("Copied") : tx("Copy link")}</button>
                <span className="muted" style={{ fontSize: 12 }}>{ptx("Expires {v0}", {v0: (new Date(issued.expiresAt).toLocaleString())})}</span>
              </div>
            </div>
          )}
        </>
      )}
      {err && <div style={{ color: 'var(--danger, #f31260)', fontSize: 13, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

/** When a recording is due to be deleted, and the switch that stops it.
 *  Retention runs on a schedule, so the date a customer will lose something
 *  belongs on the row rather than in a policy document. */
function RetentionCell({ rec }: { rec: any }) {
  usePageText();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const hold = useMutation({
    mutationFn: async (next: boolean) =>
      (await api.post(`/me/recordings/${rec.id}/legal-hold`, { hold: next })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stream'] }),
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not change the hold'),
  });

  if (rec.deletedAt) return <span className="muted">{ptx("deleted {v0}", {v0: (new Date(rec.deletedAt).toLocaleDateString())})}</span>;

  if (rec.legalHold) {
    return (
      <span className="flex gap" style={{ alignItems: 'center' }}>
        <span className="badge warn">{tx("on hold")}</span>
        <button className="btn ghost sm" disabled={hold.isPending} onClick={() => { setErr(null); hold.mutate(false); }}>{tx("release")}</button>
        {err && <span style={{ color: 'var(--danger, #f31260)', fontSize: 11 }}>{err}</span>}
      </span>
    );
  }

  const when = rec.expiresAt ? new Date(rec.expiresAt) : null;
  const soon = when ? when.getTime() - Date.now() < 7 * 86400000 : false;
  return (
    <span className="flex gap" style={{ alignItems: 'center' }}>
      <span className="muted" style={{ color: soon ? 'var(--warning, #f5a524)' : undefined }}>
        {when ? when.toLocaleDateString() : tx("kept indefinitely")}
      </span>
      <button className="btn ghost sm" title={tx("Exempt this recording from retention deletion")}
              disabled={hold.isPending} onClick={() => { setErr(null); hold.mutate(true); }}>{tx("hold")}</button>
    </span>
  );
}

/** Multi-publisher streams have a room behind them, not just a camera.
 *  The host joins it, and guests get links that let them publish without
 *  having an account here. Only rendered when the blueprint allows more than
 *  one publisher — and the API refuses to mint guest links otherwise, so the
 *  two cannot drift apart. */
function RoomPanel({ streamId, seats }: { streamId: string; seats: number }) {
  usePageText();
  const access = useProgramPermissions(streamId);
  const canOperate = access.data?.permissions.produce === true;
  const [guests, setGuests] = useState<Array<{ name: string; url: string; expiresAt: string }>>([]);
  const [guestName, setGuestName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const hostJoin = useMutation({
    mutationFn: async () => (await api.post(`/me/streams/${streamId}/sfu-token`)).data,
    onSuccess: (d) => {
      const url = `${window.location.origin}/meet/index.html#/room/${encodeURIComponent(d.room)}?token=${encodeURIComponent(d.token)}&name=Host`;
      window.open(url, '_blank', 'noopener');
    },
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not open the room'),
  });

  const invite = useMutation({
    mutationFn: async () =>
      (await api.post(`/me/streams/${streamId}/guest-invite`, { name: guestName.trim() || 'Guest' })).data,
    onSuccess: (d) => { setGuests((g) => [...g, d]); setGuestName(''); },
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not create a guest link'),
  });

  return (
    <div className="card">
      <div className="row-between" style={{ alignItems: 'baseline' }}>
        <h3 style={{ marginTop: 0 }}>{tx("Room")}</h3>
        <span className="badge">{ptx("{v0} seats", {v0: (seats)})}</span>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>{tx("This blueprint allows more than one camera. Join the room yourself, and hand out links to the people joining you — they need no account.")}</p>

      {!canOperate && <p className="muted" role="status">{access.isPending ? tx("Checking room access…") : access.isError ? tx("Room access could not be checked.") : tx("Ask an event producer or teacher to invite guests or open this room.")}{access.isError && <button className="btn ghost sm" onClick={() => void access.refetch()}>{tx("Try again")}</button>}</p>}
      <div className="flex gap" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
        <button className="btn" disabled={!canOperate || hostJoin.isPending} onClick={() => { setErr(null); hostJoin.mutate(); }}>
          {hostJoin.isPending ? tx("Opening…") : tx("Join the room")}
        </button>
      </div>

      <div className="flex gap" style={{ flexWrap: 'wrap' }}>
        <input className="input" style={{ maxWidth: 200 }} placeholder={tx("guest's name")}
               value={guestName} onChange={(e) => setGuestName(e.target.value)} />
        <button className="btn outline" disabled={!canOperate || invite.isPending} onClick={() => { setErr(null); invite.mutate(); }}>
          {invite.isPending ? tx("Creating…") : tx("Create guest link")}
        </button>
      </div>

      {guests.length > 0 && (
        <table style={{ marginTop: 12, fontSize: 13 }}>
          <tbody>
            {guests.map((g, i) => (
              <tr key={i}>
                <td style={{ paddingRight: 10 }}>{g.name}</td>
                <td className="muted" style={{ fontSize: 11, paddingRight: 10 }}>{ptx("until {v0}", {v0: (new Date(g.expiresAt).toLocaleTimeString())})}</td>
                <td>
                  <button className="btn ghost sm" onClick={() => {
                    navigator.clipboard?.writeText(g.url)
                      .then(() => setCopiedIdx(i)).catch(() => setCopiedIdx(null));
                  }}>{copiedIdx === i ? tx("copied") : tx("copy link")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {err && <div style={{ color: 'var(--danger, #f31260)', fontSize: 13, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

// Interactive tab — polls, Q&A, reactions, hand-raises for this stream.
// All data flows through the /v1/me/interactive/* endpoints we built earlier.
// ───────────────────────────────────────────────────────────────────────────

function InteractiveTab({ streamId, features }: { streamId: string; features: Record<string, boolean> }) {
  usePageText();
  return (
    <div className="workspace-interactions">
      {features.polls && <PollsCard streamId={streamId} />}
      {features.qa && <QaCard streamId={streamId} />}
      {features.reactions && <ReactionsCard streamId={streamId} />}
      {features.handRaise && <HandRaisesCard streamId={streamId} />}
    </div>
  );
}

function PollsCard({ streamId }: { streamId: string }) {
  usePageText();
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const polls = useQuery({
    queryKey: ['polls', streamId],
    queryFn: async () => (await api.get('/me/interactive/polls', { params: { streamId } })).data as any[],
    refetchInterval: 5_000,
  });
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="row-between">
        <h3 style={{ margin: 0 }}>{tx("Polls")}</h3>
        <button className="btn sm" onClick={() => setShowNew(true)}>{tx("+ New poll")}</button>
      </div>
      {polls.data?.length === 0 && (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0' }}>{tx("No polls yet for this stream.")}</div>
      )}
      {polls.data?.map((p: any) => (
        <PollRow key={p.id} poll={p} onChange={() => qc.invalidateQueries({ queryKey: ['polls', streamId] })}/>
      ))}
      {showNew && <NewPollModal streamId={streamId} onClose={() => setShowNew(false)}/>}
    </div>
  );
}

function PollRow({ poll, onChange }: { poll: any; onChange: () => void }) {
  usePageText();
  const detail = useQuery({
    queryKey: ['poll', poll.id],
    queryFn: async () => (await api.get(`/me/interactive/polls/${poll.id}`)).data,
    refetchInterval: poll.status === 'open' ? 5_000 : false,
  });
  const close = useMutation({
    mutationFn: async () => (await api.post(`/me/interactive/polls/${poll.id}/close`)).data,
    onSuccess: () => { detail.refetch(); onChange(); },
  });
  const data = detail.data ?? poll;
  const total = data.totalVotes ?? 0;
  const counts: number[] = data.counts ?? data.options.map(() => 0);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
      <div className="row-between" style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{data.question}</div>
        <span className={`badge ${data.status === 'open' ? 'success' : ''}`} style={{ fontSize: 10 }}>{data.status}</span>
      </div>
      {data.options.map((opt: string, i: number) => {
        const pct = total > 0 ? Math.round((counts[i] / total) * 100) : 0;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 12 }}>
            <span style={{ flex: 1 }}>{opt}</span>
            <div style={{ width: 80, height: 4, background: 'var(--surface-2)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--primary)' }}/>
            </div>
            <span className="muted" style={{ width: 50, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {counts[i]} ({pct}%)
            </span>
          </div>
        );
      })}
      {data.status === 'open' && (
        <button className="btn ghost sm" onClick={() => close.mutate()} style={{ marginTop: 4 }}>
          {close.isPending ? '…' : tx("Close poll")}
        </button>
      )}
    </div>
  );
}

function NewPollModal({ streamId, onClose }: { streamId: string; onClose: () => void }) {
  usePageText();
  const qc = useQueryClient();
  const [question, setQuestion] = useState('');
  const [optionText, setOptionText] = useState('');
  const [multiSelect, setMultiSelect] = useState(false);
  const [err, setErr] = useState('');

  const options = optionText.split('\n').map(s => s.trim()).filter(Boolean);

  const create = useMutation({
    mutationFn: async () => (await api.post('/me/interactive/polls', {
      streamId, question, options, multiSelect,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['polls', streamId] }); onClose(); },
    onError: (e: any) => setErr(e?.response?.data?.message?.[0] || e?.response?.data?.message || 'Failed'),
  });

  return (
    <Modal onClose={onClose}>
      <h2>{tx("New poll")}</h2>
      <p className="modal-subtitle">{tx("Audience will vote via the chat WS or your client.")}</p>
      <label className="label">{tx("Question")}</label>
      <input className="input" autoFocus value={question} onChange={(e) => setQuestion(e.target.value)}
             placeholder={tx("Which color should we ship first?")}/>
      <label className="label" style={{ marginTop: 12 }}>{tx("Options (one per line, 2–8)")}</label>
      <textarea className="input" rows={5} value={optionText} onChange={(e) => setOptionText(e.target.value)}
                placeholder={tx("Black\nWhite\nOlive\nCream")}/>
      <label className="row gap" style={{ marginTop: 12, fontSize: 13, cursor: 'pointer' }}>
        <input type="checkbox" checked={multiSelect} onChange={(e) => setMultiSelect(e.target.checked)}/>
        <span>{tx("Allow multi-select")}</span>
      </label>
      {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
      <div className="actions">
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn" disabled={!question || options.length < 2 || create.isPending}
                onClick={() => create.mutate()}>
          {create.isPending ? tx("Creating…") : tx("Create poll")}
        </button>
      </div>
    </Modal>
  );
}

function QaCard({ streamId }: { streamId: string }) {
  usePageText();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['qa', streamId],
    queryFn: async () => (await api.get('/me/interactive/qa', { params: { streamId } })).data as any[],
    refetchInterval: 5_000,
  });
  const action = (path: string) => useMutation({
    mutationFn: async (id: string) => (await api.post(`/me/interactive/qa/${id}${path}`, {})).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['qa', streamId] }),
  });
  const markLive = action('/mark-live');
  const answer = action('/answer');
  const dismiss = action('/dismiss');
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h3 style={{ margin: 0 }}>{tx("Q&A queue")}</h3>
      {list.data?.length === 0 && (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0' }}>{tx("No questions yet.")}</div>
      )}
      {list.data?.map((q: any) => (
        <div key={q.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
          <div className="row-between" style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 13 }}>
              <strong>{q.askerName || tx("Guest")}</strong>
              <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>{ptx("· {v0} upvote{v1}", {v0: (q.upvotes), v1: (q.upvotes !== 1 ? 's' : '')})}</span>
            </div>
            <span className={`badge ${q.status === 'live' ? 'success' : ''}`} style={{ fontSize: 10 }}>{q.status}</span>
          </div>
          <div style={{ fontSize: 13.5, marginBottom: 8 }}>{q.body}</div>
          {q.answerText && (
            <div className="muted" style={{ fontSize: 12, padding: 8, background: 'var(--surface-2)', borderRadius: 6, marginBottom: 8 }}>
              <strong>{tx("Answer:")}</strong> {q.answerText}
            </div>
          )}
          {q.status !== 'answered' && q.status !== 'dismissed' && (
            <div className="flex gap" style={{ fontSize: 11 }}>
              {q.status !== 'live' && (
                <button className="btn outline sm" onClick={() => markLive.mutate(q.id)}>{tx("Answering now")}</button>
              )}
              <button className="btn outline sm" onClick={() => answer.mutate(q.id)}>{tx("Mark answered")}</button>
              <button className="btn ghost sm" style={{ color: 'var(--danger)' }} onClick={() => dismiss.mutate(q.id)}>{tx("Dismiss")}</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ReactionsCard({ streamId }: { streamId: string }) {
  usePageText();
  const totals = useQuery({
    queryKey: ['reactions', streamId],
    queryFn: async () => (await api.get(`/me/interactive/reactions/${streamId}`)).data,
    refetchInterval: 8_000,
  });
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h3 style={{ margin: 0 }}>{tx("Reactions")}</h3>
      {totals.data && Object.keys(totals.data.byKind || {}).length === 0 && (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0' }}>{tx("No reactions yet for this stream.")}</div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {totals.data?.byKind && Object.entries(totals.data.byKind).map(([kind, count]: [string, any]) => (
          <div key={kind} style={{
            background: 'var(--surface-2)', padding: '10px 16px',
            borderRadius: 8, minWidth: 90, textAlign: 'center', border: '1px solid var(--border)',
          }}>
            <div style={{ fontSize: 22 }}>{kind}</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{count}</div>
          </div>
        ))}
      </div>
      {totals.data?.total > 0 && (
        <div className="muted" style={{ fontSize: 12 }}>{tx("Total: ")}<strong style={{ color: 'var(--text)' }}>{totals.data.total}</strong> ·{' '}{tx("Per-minute series available via the API (graph it server-side)")}</div>
      )}
    </div>
  );
}

function HandRaisesCard({ streamId }: { streamId: string }) {
  usePageText();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['hand-raises', streamId],
    queryFn: async () => (await api.get(`/me/interactive/hand-raises/${streamId}`)).data as any[],
    refetchInterval: 5_000,
  });
  const promote = useMutation({
    mutationFn: async (id: string) => (await api.post(`/me/interactive/hand-raises/${id}/promote`, {})).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hand-raises', streamId] }),
  });
  const lower = useMutation({
    mutationFn: async (id: string) => (await api.post(`/me/interactive/hand-raises/${id}/lower`, {})).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hand-raises', streamId] }),
  });
  const mintToken = useMutation({
    mutationFn: async (raiser: { externalUserId: string; displayName: string }) =>
      (await api.post(`/me/streams/${streamId}/guest-invite`, { name: raiser.displayName, minutes: 60 })).data,
  });
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h3 style={{ margin: 0 }}>{tx("Hand-raise queue")}</h3>
      {(promote.isError || lower.isError || mintToken.isError) && <p role="alert">{tx("Could not update this request. Please try again.")}</p>}
      {list.data?.length === 0 && (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0' }}>{tx("No raised hands. Viewers can request to speak from the event page.")}</div>
      )}
      {list.data?.map((hr: any) => (
        <div key={hr.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
          <div className="row-between" style={{ marginBottom: 4 }}>
            <strong style={{ fontSize: 13 }}>{hr.raiserName || hr.raiserId}</strong>
            <span className={`badge ${hr.status === 'promoted' ? 'success' : ''}`} style={{ fontSize: 10 }}>{hr.status}</span>
          </div>
          {hr.note && <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>"{hr.note}"</div>}
          <div className="flex gap" style={{ fontSize: 11 }}>
            {hr.status === 'queued' && (
              <button className="btn sm" disabled={promote.isPending} onClick={() => promote.mutate(hr.id)}>{tx("Approve request")}</button>
            )}
            {hr.status === 'promoted' && (
              <button className="btn outline sm" disabled={mintToken.isPending} onClick={() => mintToken.mutate({ externalUserId: hr.raiserId, displayName: hr.raiserName || 'Guest' })}>
                {mintToken.isPending ? tx("Creating…") : tx("Create guest link")}
              </button>
            )}
            <button className="btn ghost sm" disabled={lower.isPending} onClick={() => lower.mutate(hr.id)} style={{ color: 'var(--danger)' }}>{tx("Lower hand")}</button>
          </div>
          {mintToken.data && mintToken.variables?.externalUserId === hr.raiserId && (
            <div className="code" style={{ fontSize: 11, padding: '6px 10px', marginTop: 8, wordBreak: 'break-all' }}>
              <span className="val">{mintToken.data.url}</span>
              <button className="copy" onClick={() => navigator.clipboard.writeText(mintToken.data.url)}>{tx("Copy guest link")}</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Restream tab — show saved targets, attach/detach to this stream, see live route status.
// ───────────────────────────────────────────────────────────────────────────

// ───────────────────────────────────────────────────────────────────────────
// Recordings tab — now with "Cut clip" action per recording.
// ───────────────────────────────────────────────────────────────────────────

function RecordingsTab({ recordings, recordingEnabled, streamId }: { recordings: any[]; recordingEnabled: boolean; streamId: string }) {
  usePageText();
  const [cutting, setCutting] = useState<any | null>(null);

  if (recordings.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <div className="muted">
          {recordingEnabled
            ? tx("No recordings yet — recordings appear after you stream.")
            : tx("Recording is disabled for this stream. Enable it in Settings.")}
        </div>
      </div>
    );
  }

  return (
    <>
      <ReplayLibrary recordings={recordings} streamId={streamId} onCut={setCutting} footer={r => <RetentionCell rec={r} />} />
      {cutting && <CutClipModal recording={cutting} onClose={() => setCutting(null)}/>}
    </>
  );
}

function CutClipModal({ recording, onClose }: { recording: any; onClose: () => void }) {
  usePageText();
  const qc = useQueryClient();
  const totalSec = recording.durationMs / 1000;
  const [startSec, setStartSec] = useState(0);
  const [endSec, setEndSec] = useState(Math.min(30, totalSec));
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [err, setErr] = useState('');

  const create = useMutation({
    mutationFn: async () => (await api.post('/me/clips', {
      recordingId: recording.id,
      startSec, endSec,
      title: title || undefined,
      description: description || undefined,
      isPublic,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clips'] }); onClose(); },
    onError: (e: any) => setErr(e?.response?.data?.message || 'Failed to create clip'),
  });

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  };

  return (
    <Modal onClose={onClose}>
      <h2>{tx("Cut a clip")}</h2>
      <p className="modal-subtitle">{ptx("Recording duration: {v0}. Pick an in/out window (max 10 minutes).", {v0: (<strong>{fmt(totalSec)}</strong>)})}</p>

      <div className="grid-2" style={{ marginTop: 4 }}>
        <div>
          <label className="label">{tx("Start (seconds)")}</label>
          <input className="input mono" type="number" min={0} max={totalSec} step={0.1}
                 value={startSec} onChange={(e) => setStartSec(Math.max(0, +e.target.value))}/>
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{fmt(startSec)}</div>
        </div>
        <div>
          <label className="label">{tx("End (seconds)")}</label>
          <input className="input mono" type="number" min={startSec + 0.5} max={totalSec} step={0.1}
                 value={endSec} onChange={(e) => setEndSec(Math.min(totalSec, +e.target.value))}/>
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            {fmt(endSec)}{tx(" · clip = ")}{(endSec - startSec).toFixed(1)}s
          </div>
        </div>
      </div>

      <label className="label" style={{ marginTop: 12 }}>{tx("Title (optional)")}</label>
      <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={tx("Funniest moment")}/>

      <label className="label" style={{ marginTop: 12 }}>{tx("Description (optional)")}</label>
      <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder={tx("Context for the clip")}/>

      <label className="row gap" style={{ marginTop: 12, fontSize: 13, cursor: 'pointer' }}>
        <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)}/>
        <span>{tx("Public sharing — anyone with the link can play")}</span>
      </label>

      {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}

      <div className="actions">
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn"
                disabled={endSec - startSec < 0.5 || endSec - startSec > 600 || create.isPending}
                onClick={() => create.mutate()}>
          {create.isPending ? tx("Queuing…") : ptx("Cut {v0}s clip →", {v0: ((endSec - startSec).toFixed(1))})}
        </button>
      </div>
    </Modal>
  );
}
